import React from 'react';
import type { TimeSignature } from '../../lib/rhythm/ecoRitmoConfig';
import type { MelodiaSessionConfig, MelodySoundId } from '../../lib/melody/melodiaBuilderConfig';

interface MelodiaGlobalSettingsProps {
  config: MelodiaSessionConfig;
  onTimeSignature: (ts: TimeSignature) => void;
  onBpm: (bpm: number) => void;
  onSound: (soundId: MelodySoundId) => void;
  canPlay: boolean;
  isPlaying: boolean;
  onPlay: () => void;
  onStop: () => void;
}

const TIME_SIGNATURES: TimeSignature[] = ['2/4', '3/4', '4/4'];

const SOUNDS: { id: MelodySoundId; emoji: string; label: string }[] = [
  { id: 'piano', emoji: '🥁', label: 'Piano' },
  { id: 'metalofono', emoji: '🔔', label: 'Metalófono' },
  { id: 'flauta', emoji: '🪇', label: 'Flauta' },
];

export const MelodiaGlobalSettings: React.FC<MelodiaGlobalSettingsProps> = ({
  config,
  onTimeSignature,
  onBpm,
  onSound,
  canPlay,
  isPlaying,
  onPlay,
  onStop,
}) => {
  return (
    <footer className="melodia-settings barra-inferior shrink-0 flex items-stretch gap-2 sm:gap-3">
      <div className="melodia-settings-grid flex-1 min-w-0 grid grid-cols-3 gap-2 sm:gap-3 items-center">
        <div className="flex flex-col gap-1">
          <span className="eco-settings-label">Compás</span>
          <div className="flex gap-1">
            {TIME_SIGNATURES.map((ts) => {
              const sel = config.timeSignature === ts;
              return (
                <button
                  key={ts}
                  type="button"
                  disabled={isPlaying}
                  onClick={() => onTimeSignature(ts)}
                  className={`eco-chip flex-1 py-1.5 ${sel ? 'eco-chip--active' : ''}`}
                >
                  {ts}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between gap-1">
            <span className="eco-settings-label">Tempo</span>
            <span className="eco-chip-count">{config.bpm}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-sm leading-none opacity-60" aria-hidden>
              🐢
            </span>
            <input
              type="range"
              min={50}
              max={120}
              step={2}
              value={config.bpm}
              disabled={isPlaying}
              onChange={(e) => onBpm(Number(e.target.value))}
              className="eco-tempo-slider flex-1 min-w-0"
              aria-label="Tempo"
            />
            <span className="text-sm leading-none opacity-60" aria-hidden>
              🐇
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <span className="eco-settings-label">Sonido</span>
          <div className="flex gap-1">
            {SOUNDS.map((s) => {
              const sel = config.soundId === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  disabled={isPlaying}
                  onClick={() => onSound(s.id)}
                  className={`eco-chip flex-1 py-1 text-lg leading-none ${sel ? 'eco-chip--active' : ''}`}
                  title={s.label}
                  aria-label={s.label}
                >
                  {s.emoji}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {isPlaying ? (
        <button
          type="button"
          onClick={onStop}
          className="melodia-stop-bottom shrink-0 flex flex-col items-center justify-center gap-0.5 transition-all hover:-translate-y-0.5 active:scale-[0.98]"
          aria-label="Detener"
        >
          <span className="melodia-play-bottom-icon flex items-center justify-center text-xl">■</span>
          <span className="text-[11px] font-semibold tracking-wide">Stop</span>
        </button>
      ) : (
        <button
          type="button"
          onClick={() => canPlay && onPlay()}
          disabled={!canPlay}
          className="eco-play-bottom shrink-0 flex flex-col items-center justify-center gap-0.5 disabled:opacity-35 disabled:pointer-events-none transition-all hover:-translate-y-0.5 active:scale-[0.98]"
          aria-label="Play"
        >
          <span className="eco-play-bottom-icon flex items-center justify-center text-xl pl-0.5">▶</span>
          <span className="text-[11px] font-semibold tracking-wide">Play</span>
        </button>
      )}
    </footer>
  );
};

export default MelodiaGlobalSettings;
